import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  min-height: 700px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  overflow: hidden;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
    opacity: 0.8;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 4rem;
    padding: 6rem 2rem;
  }
`;

const HeroText = styled.div``;

const AutomationBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 2.5rem;
  line-height: 1.7;
  opacity: 0.9;
`;

const AutomationIcon = styled(motion.div)`
  position: relative;
  width: 500px;
  height: 500px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
    border-radius: 50%;
    animation: pulse 3s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.2;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.5;
    }
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

const IconCircle = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 180px;
  height: 180px;
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 
    0 0 0 20px rgba(34, 197, 94, 0.1),
    0 0 0 40px rgba(34, 197, 94, 0.05),
    0 10px 30px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 70%);
  }
`;

const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  gap: 2rem;
  padding: 2.5rem;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(34, 197, 94, 0.2);
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
    transform: scale(1.1);
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const WorkflowSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.08) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
  }
`;

const WorkflowGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const WorkflowCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(34, 197, 94, 0.2);
  }

  .step-number {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: #22c55e;
    margin: 0 auto 1.5rem;
    transition: all 0.3s ease;
  }

  &:hover .step-number {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
    transform: scale(1.1);
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  p {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.6;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;

const CTASection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(34, 197, 94, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  color: white;
  padding: 1.5rem 4rem;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  }
`;

function AutomationTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Automation Testing', path: '/services/automation' }
      ]} />
      <Hero>
        <HeroContent>
          <HeroText>
            <AutomationBadge
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              ⚡ Automation Testing
            </AutomationBadge>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Accelerate Testing with Automation
            </Title>
            <Subtitle>
              Streamline your testing process with intelligent automation solutions.
              Faster deployment, better quality, and continuous testing.
            </Subtitle>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Automating Tests
            </CTAButton>
          </HeroText>
          <AutomationIcon
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.4,
              type: "spring",
              stiffness: 100
            }}
          >
            <IconCircle
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{
                duration: 1,
                delay: 0.6,
                type: "spring",
                stiffness: 100
              }}
            >
              <svg 
                width="80" 
                height="80" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </IconCircle>
          </AutomationIcon>
        </HeroContent>
      </Hero>

      <FeaturesSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Powerful <span>Automation Features</span>
          </SectionTitle>
          <SectionSubtitle>
            Comprehensive automation solutions to streamline your testing process
          </SectionSubtitle>
        </SectionHeader>
        <FeatureGrid>
          {[
            {
              icon: '🔄',
              title: 'Continuous Integration',
              description: 'Seamlessly integrate automated tests into your CI/CD pipeline.'
            },
            {
              icon: '🤖',
              title: 'Test Script Generation',
              description: 'Automatically generate test scripts from user interactions.'
            },
            {
              icon: '📊',
              title: 'Detailed Reports',
              description: 'Get comprehensive test reports and analytics.'
            },
            {
              icon: '⚡',
              title: 'Parallel Testing',
              description: 'Run multiple tests simultaneously for faster results.'
            },
            {
              icon: '🔍',
              title: 'Cross-browser Testing',
              description: 'Automated testing across all major browsers.'
            },
            {
              icon: '📱',
              title: 'Mobile Automation',
              description: 'Automated testing for mobile devices and apps.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>

      <WorkflowSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Automation <span>Workflow</span>
          </SectionTitle>
          <SectionSubtitle>
            Simple steps to implement test automation in your development process
          </SectionSubtitle>
        </SectionHeader>
        <WorkflowGrid>
          {[
            {
              step: '01',
              title: 'Test Planning',
              description: 'Define test scenarios and automation strategy'
            },
            {
              step: '02',
              title: 'Script Creation',
              description: 'Develop and optimize test scripts'
            },
            {
              step: '03',
              title: 'Integration',
              description: 'Integrate with CI/CD pipeline'
            },
            {
              step: '04',
              title: 'Execution & Reports',
              description: 'Run tests and analyze results'
            }
          ].map((workflow, index) => (
            <WorkflowCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="step-number">{workflow.step}</div>
              <h3>{workflow.title}</h3>
              <p>{workflow.description}</p>
            </WorkflowCard>
          ))}
        </WorkflowGrid>
      </WorkflowSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Automate Your Testing?</CTATitle>
          <CTAText>
            Start automating your tests today and accelerate your development process
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started Free
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default AutomationTesting;